import { baseApi } from '@/store/api/baseApi';
import type {
  BulkCouponIdsPayload,
  BulkCouponStatusPayload,
  Coupon,
  CouponListParams,
  CreateCouponPayload,
  PaginatedCoupons,
  UpdateCouponPayload,
} from '@/types/coupon';

function buildCouponListQuery(params: CouponListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'updatedAt');
  searchParams.set('sortOrder', params.sortOrder ?? 'desc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status) searchParams.set('status', params.status);
  if (params.discountType) searchParams.set('discountType', params.discountType);

  return searchParams.toString();
}

export const couponsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCoupon: builder.query<Coupon, string>({
      query: (id) => `/coupons/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Coupons', id }],
    }),
    getCoupons: builder.query<PaginatedCoupons, CouponListParams>({
      query: (params) => ({
        url: `/coupons?${buildCouponListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildCouponListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildCouponListQuery(currentArg ?? {}) !== buildCouponListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Coupons' as const, id })),
              { type: 'Coupons', id: 'LIST' },
            ]
          : [{ type: 'Coupons', id: 'LIST' }],
    }),
    createCoupon: builder.mutation<Coupon, CreateCouponPayload>({
      query: (body) => ({ url: '/coupons/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Coupons', id: 'LIST' }],
    }),
    updateCoupon: builder.mutation<Coupon, { id: string; body: UpdateCouponPayload }>({
      query: ({ id, body }) => ({ url: `/coupons/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Coupons', id },
        { type: 'Coupons', id: 'LIST' },
      ],
    }),
    deleteCoupon: builder.mutation<void, string>({
      query: (id) => ({ url: `/coupons/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Coupons', id: 'LIST' }],
    }),
    bulkUpdateCouponStatus: builder.mutation<{ count: number }, BulkCouponStatusPayload>({
      query: (body) => ({ url: '/coupons/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Coupons', id: 'LIST' }],
    }),
    bulkDeleteCoupons: builder.mutation<{ count: number }, BulkCouponIdsPayload>({
      query: (body) => ({ url: '/coupons/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Coupons', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetCouponQuery,
  useGetCouponsQuery,
  useCreateCouponMutation,
  useUpdateCouponMutation,
  useDeleteCouponMutation,
  useBulkUpdateCouponStatusMutation,
  useBulkDeleteCouponsMutation,
} = couponsApi;
